/**
 * Hooks des tableaux de bord (docs/12 §6).
 *
 * Trois lectures, trois publics — et trois hooks plutôt qu'un seul paramétré :
 * les réponses n'ont pas la même forme parce qu'elles ne racontent pas la même
 * chose, et un hook unique gouverné par le rôle de l'appelant ferait croire au
 * contraire.
 *
 * ⚠️ **Aucun chiffre n'est calculé ici.** Les taux de complétion, la moyenne
 * d'avancement et le taux de réussite d'un exercice viennent du serveur, arrondis
 * une fois. Deux écrans qui diviseraient chacun de leur côté afficheraient deux
 * taux différents pour le même cours.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, unwrap } from "@/shared/api/client";
import type { ApiError } from "@/shared/api/errors";
import type { components } from "@/shared/api/generated";
import { learningKeys, teachingKeys } from "@/shared/api/query-keys";

export type LearnerDashboard = components["schemas"]["LearnerDashboardResponse"];
export type TeacherDashboard = components["schemas"]["TeacherDashboardResponse"];
export type StudentFile = components["schemas"]["StudentFileResponse"];
export type CourseHealth = components["schemas"]["CourseHealthResponse"];
export type StrugglingStudent = components["schemas"]["StrugglingStudentResponse"];
export type WeakExercise = components["schemas"]["WeakExerciseResponse"];
export type PendingResult = components["schemas"]["PendingResultResponse"];
export type RecentResult = components["schemas"]["RecentResultResponse"];

export function useLearnerDashboard(): UseQueryResult<LearnerDashboard, ApiError> {
  return useQuery({
    queryKey: learningKeys.dashboard(),
    queryFn: () => unwrap(api.GET("/api/v1/me/dashboard", {})),
  });
}

export function useTeacherDashboard(): UseQueryResult<TeacherDashboard, ApiError> {
  return useQuery({
    queryKey: teachingKeys.dashboard(),
    queryFn: () => unwrap(api.GET("/api/v1/teaching/dashboard", {})),
  });
}

/**
 * La fiche d'un élève (RM-05 / RM-06).
 *
 * `retry: false` : la réponse pour un élève qui n'est pas le sien est un 404 —
 * délibérément, un 403 confirmerait que le compte existe —, et le réessayer ne
 * changera pas la réponse.
 */
export function useStudentFile(
  studentId: string | undefined,
): UseQueryResult<StudentFile, ApiError> {
  return useQuery({
    queryKey: teachingKeys.student(studentId ?? ""),
    enabled: studentId !== undefined,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/api/v1/teaching/students/{student_id}", {
          params: { path: { student_id: studentId ?? "" } },
        }),
      ),
  });
}
